import {
  MASTER_IMPORT_ORDER,
  matchIndianState,
  normalizeReasonType,
  normalizeStatus,
} from "./mastersImportTemplate";

/**
 * Client-side validate masters workbook rows (no writes).
 * Returns { validRecords, invalidRecords } for ImportValidationReview.
 */
export function validateMastersRows(bySheet, existing = {}) {
  const validRecords = [];
  const invalidRecords = [];

  const existingSets = {
    Branch: toNameSet(existing.branches),
    Department: toNameSet(existing.departments),
    Grade: toNameSet(existing.grades),
    Category: toNameSet(existing.categories),
    Reasons: toReasonSet(existing.reasons),
    "Sub Department": toDeptScopedSet(existing.subDepartments),
    Designation: toDeptScopedSet(existing.designations),
  };

  const deptByName = new Map();
  (existing.departments || []).forEach((d) => {
    if (d?.name) deptByName.set(String(d.name).trim().toLowerCase(), d.id);
  });

  const seen = {
    Branch: new Set(),
    Department: new Set(),
    Grade: new Set(),
    Category: new Set(),
    Reasons: new Set(),
    "Sub Department": new Set(),
    Designation: new Set(),
  };

  for (const sheetName of MASTER_IMPORT_ORDER) {
    const rows = bySheet[sheetName] || [];
    for (const row of rows) {
      const excelRow = row._row;
      const record = {
        Name: row.name,
        "Employee Code": sheetName,
        "Employee Name": row.name,
        sheetName,
        ...row,
      };
      const fieldErrors = {};

      if (!row.name) {
        fieldErrors.Name = "Name is required";
      }

      if (sheetName === "Branch") {
        const nameKey = String(row.name || "").toLowerCase();
        if (nameKey && seen.Branch.has(nameKey)) {
          fieldErrors.Name = `Duplicate Branch name in file: ${row.name}`;
        } else if (nameKey) seen.Branch.add(nameKey);
        if (nameKey && existingSets.Branch.has(nameKey)) {
          fieldErrors.Name = `Branch “${row.name}” already exists`;
        }
        const status = normalizeStatus(row.status || "Active");
        if (!status) fieldErrors.Status = `Invalid Status “${row.status}” (use Active/Inactive)`;
        else row._status = status;
        const stateRes = matchIndianState(row.state);
        if (!stateRes.ok) {
          fieldErrors.State = row.state
            ? `Invalid State “${row.state}” — pick from the template list`
            : "State is required";
        } else {
          row._state = stateRes.value;
        }
      } else if (
        sheetName === "Department" ||
        sheetName === "Grade" ||
        sheetName === "Category"
      ) {
        const nameKey = String(row.name || "").toLowerCase();
        if (nameKey && seen[sheetName].has(nameKey)) {
          fieldErrors.Name = `Duplicate ${sheetName} name in file: ${row.name}`;
        } else if (nameKey) seen[sheetName].add(nameKey);
        if (nameKey && existingSets[sheetName].has(nameKey)) {
          fieldErrors.Name = `${sheetName} “${row.name}” already exists`;
        }
        const status = normalizeStatus(row.status || "Active");
        if (!status) fieldErrors.Status = `Invalid Status “${row.status}”`;
        else row._status = status;
      } else if (sheetName === "Reasons") {
        const type = normalizeReasonType(row.type);
        if (!type) {
          fieldErrors.Type = `Invalid Type “${row.type}” — use Resignation or Termination`;
        } else {
          row._type = type;
        }
        const nameKey = `${type || ""}:${String(row.name || "").toLowerCase()}`;
        if (type && seen.Reasons.has(nameKey)) {
          fieldErrors.Name = `Duplicate Reason in file: ${row.name} (${type})`;
        } else if (type) seen.Reasons.add(nameKey);
        if (type && existingSets.Reasons.has(nameKey)) {
          fieldErrors.Name = `Reason “${row.name}” (${type}) already exists`;
        }
        const status = normalizeStatus(row.status || "Active");
        if (!status) fieldErrors.Status = `Invalid Status “${row.status}”`;
        else row._status = status;
      } else if (sheetName === "Sub Department" || sheetName === "Designation") {
        const deptKey = String(row.department || "").trim().toLowerCase();
        let deptId = deptByName.get(deptKey) || null;
        // Department may be created later from this same file
        const deptInFile = (bySheet.Department || []).some(
          (d) => String(d.name || "").trim().toLowerCase() === deptKey
        );
        if (!deptId && !deptInFile) {
          fieldErrors.Department = row.department
            ? `Department “${row.department}” not found — create it first or include it in the Department sheet`
            : "Department is required";
        }
        row._deptKey = deptKey;
        row._deptId = deptId;
        const nameKey = `${deptKey}:${String(row.name || "").toLowerCase()}`;
        if (seen[sheetName].has(nameKey)) {
          fieldErrors.Name = `Duplicate ${sheetName} in file: ${row.name}`;
        } else seen[sheetName].add(nameKey);
        if (existingSets[sheetName].has(nameKey)) {
          fieldErrors.Name = `${sheetName} “${row.name}” already exists for this department`;
        }
        const status = normalizeStatus(row.status || "Active");
        if (!status) fieldErrors.Status = `Invalid Status “${row.status}”`;
        else row._status = status;
      }

      if (Object.keys(fieldErrors).length) {
        invalidRecords.push({
          row: excelRow,
          record: { ...record, Name: `${sheetName}: ${row.name || ""}` },
          fieldErrors,
          error: Object.values(fieldErrors).join("; "),
        });
      } else {
        validRecords.push({
          __row: excelRow,
          row: excelRow,
          sheetName,
          record: { ...record, Name: `${sheetName}: ${row.name}` },
          normalizedRecord: { ...row, sheetName },
        });
      }
    }
  }

  return { validRecords, invalidRecords };
}

function toNameSet(list) {
  const set = new Set();
  (list || []).forEach((item) => {
    const n = String(item?.name || item || "").trim().toLowerCase();
    if (n) set.add(n);
  });
  return set;
}

function toReasonSet(list) {
  const set = new Set();
  (list || []).forEach((item) => {
    const name = String(item?.name || "").trim().toLowerCase();
    const type = String(item?.type || "").trim();
    if (name) set.add(`${type}:${name}`);
  });
  return set;
}

function toDeptScopedSet(list) {
  const set = new Set();
  (list || []).forEach((item) => {
    const name = String(item?.name || "").trim().toLowerCase();
    const dept =
      String(item?.department?.name || item?.departmentName || "").trim().toLowerCase() ||
      String(item?.departmentId || "");
    if (name) set.add(`${dept}:${name}`);
  });
  return set;
}
